import React, { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  CheckboxVisibility,
  ConstrainMode,
  ContextualMenu,
  ContextualMenuItemType,
  DefaultButton,
  DetailsHeader,
  DetailsList,
  DetailsListLayoutMode,
  DirectionalHint,
  IButtonStyles,
  IColumnReorderOptions,
  ICommandBarItemProps,
  IContextualMenuItem,
  IContextualMenuProps,
  IDetailsGroupRenderProps,
  IDetailsHeaderProps,
  IDetailsListStyles,
  IGroup,
  IObjectWithKey,
  IColumn as IOfficeColumn,
  Icon,
  Link,
  ScrollablePane,
  Selection,
  SelectionMode,
  Sticky,
  StickyPositionType,
} from "@fluentui/react";
import { LoadingOverlay, TooltipHost, debounce, useTheme } from "cayo.ui";
import styled from "styled-components";
import { Entity, PropertyAnnotation } from "@cayo/shared/api/types/cayo-graph";
import { IDetailsList, GridColumnFiltersConfig } from "@cayo/shared/api/types/schema";
import { useSchemes } from "@cayo/shared/components/App/hooks";
import { ColumnFilters } from "@cayo/shared/components/GridContainer/components/ColumnFilters";
import { useColumnFilters } from "@cayo/shared/components/GridContainer/components/ColumnFilters/hooks/useColumnFilters";
import { IQuery, DispatchAction } from "@cayo/shared/components/GridContainer/types";
import {
  ColumnFiltersState,
  OnColumnFilterChange,
} from "@cayo/shared/components/GridContainer/types/columnFilters";
import logger from "@cayo/shared/libs/logger";
import { objectUtils } from "@cayo/shared/utils/object";
import { ICommandBarItem } from "../../api/schema.api";
import { gridUtils } from "../../utils/grid-utils";
import { BlankSearchResult } from "../BlankSearchResult";
import { CancellationPanel } from "../CancellationPanel";
import useTypeAnnotations from "../GlobalHooks/type-annotations.hook";
import ContextualMenuItem from "./components/ContextualMenuItem";
import { useAdjustColumns } from "./hooks/useAdjustColumns";
import renderGridCell from "./renderers/gridCellRenderer";
import { renderRow } from "./renderers/renderRow";

export type GridDetailsProps = {
  propertyAnnotations: PropertyAnnotation[] | undefined;
  columns: Array<IOfficeColumn>;
  ajustColumnsToken: string;
  query: IQuery;
  items: Array<any>;
  groups?: Array<IGroup>;
  itemMenu?: Array<IContextualMenuItem>;
  dispatch: DispatchAction;
  isLoading: boolean | null;
  schema: IDetailsList;
  gridSettingsKey: string;
  skipToken: string | null;
  selectedItems?: Entity[];
  previewQueryId?: string | null;
  onItemClick?: (item?: Entity) => void;
  abortControllerState?: AbortController | null;
  setAbortControllerState: (props: AbortController | null) => void;
  commandBarRef?: RefObject<{ itemsRef: RefObject<ICommandBarItemProps[]> }>;
};

const log = logger.getLogger("QUICK-FILTER-NEW GridDetails");

export function GridDetails(props: GridDetailsProps): JSX.Element {
  const intl = useIntl();
  const theme = useTheme();
  const typeAnnotations = useTypeAnnotations();
  const { showPropertiesPanel } = useSchemes();

  const [contextualMenuProps, setContextualMenuProps] = useState<IContextualMenuProps>();

  const { ref: gridRef, adjustColumns } = useAdjustColumns(props.columns);
  const { columnFilters, onColumnFilterChange, filteredItems, filteredGroups } = useColumnFilters({
    items: props.items,
    groups: props.groups,
  });

  const { commandBarRef } = props;

  const onItemClicked = useCallback(
    (path?: string, query?: string, item?: any) => {
      log.debug("onItemClicked -> path, query, item", path, query, item);

      showPropertiesPanel({ id: item, parentId: props.schema?.id });

      return Promise.resolve();
    },
    [props.schema?.id]
  );

  const onColumnHeaderClick = useCallback(
    (_?: React.MouseEvent<HTMLElement>, column?: IOfficeColumn) => {
      if (!column) {
        return;
      }
      log.debug("onColumnHeaderClick.sort -> column", column);
      props.dispatch({
        kind: "sort",
        orderBy: [column.fieldName!, !column.isSorted ? true : !!column.isSortedDescending],
      });
    },
    [props.dispatch]
  );

  const handleColumnReorder = (fromIndex: number, toIndex: number) => {
    const fields = props.columns.map((c) => c.fieldName!);
    log.debug("handleColumnReorder -> fromIndex, toIndex, fields", fromIndex, toIndex, [...fields]);
    const draggedField = fields[fromIndex];
    // insert before the dropped item
    fields.splice(fromIndex, 1);
    fields.splice(toIndex, 0, draggedField);
    log.debug("handleColumnReorder -> reordered fields", [...fields]);
    props.dispatch({ kind: "applyColumns", columns: fields });
  };

  const getColumnReorderOptions = (): IColumnReorderOptions => {
    return { handleColumnReorder };
  };

  function onItemClick(item?: any) {
    if (props.onItemClick) {
      return props.onItemClick(item);
    }
    const defaultAction = commandBarRef?.current?.itemsRef.current?.find((i) => i.data?.isDefault);
    if (defaultAction) {
      defaultAction.onClick?.(undefined, defaultAction);
    } else {
      props.dispatch({
        kind: "showProps",
        item,
        parentId: props.schema.id!,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onClose: (updateItem?: Entity) => {
          // TODO:  if (updateItem?.objectType !== item?.objectType) {
          if (props.schema.notificationMode !== "off") {
            props.dispatch({ kind: "refresh" });
          }
          // }
        },
      });
    }
  }

  function onColumnResize(column?: IOfficeColumn, newWidth?: number) {
    const existingColumn = props.columns.find((cc) => cc.fieldName === column?.fieldName);
    if (existingColumn) {
      existingColumn.currentWidth = existingColumn.calculatedWidth = newWidth;
      gridUtils.saveGridColumns(props.gridSettingsKey, props.columns); //TODO: move to controller
    }
  }

  useEffect(function mount() {
    log.debug(".mount", props);
  }, []);

  useEffect(
    function ajustColumns() {
      log.debug("usf.resetColumns -> props.resetColumnsToken", props.ajustColumnsToken);
      if (props.ajustColumnsToken.length === 0 || props.items.length === 0) {
        return;
      }

      debounce(
        () =>
          adjustColumns(
            (adjustedColumns) => {
              log.debug(
                "usf.resetColumns -> adjustColumns.onAjusted -> adjustedColumns",
                adjustedColumns
              );
              gridUtils.saveGridColumns(props.gridSettingsKey, adjustedColumns);
            },
            (value) => log.debug("usf.resetColumns -> adjustColumns.onLoading -> value", value)
          ),
        100
      )();
    },
    [props.ajustColumnsToken]
  );

  useEffect(
    function clearSelected() {
      log.debug("usf.clearSelected -> props.selectedItems.length", props.selectedItems?.length);
      if (!props.selectedItems?.length) {
        selection.setAllSelected(false);
      }
    },
    [props.selectedItems?.length]
  );

  const selection = useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          const selectedItems = selection.getSelection();
          log.debug("selection.onSelectionChanged -> ", selectedItems);
          props.dispatch({ kind: "changedSelectedItems", items: selectedItems });
          if (props.schema?.invokeItemByOneClick && selectedItems.length === 1) {
            onItemClick(selectedItems[0]);
          }
        },
        getKey: (item: IObjectWithKey) => (item as any)["id"],
      }),
    []
  );

  const onItemContextMenu = useCallback(
    (_item?: any, index?: number, ev?: Event): boolean => {
      if ((ev as MouseEvent)?.ctrlKey) {
        return true;
      }

      const menuItems = objectUtils.deepClone(commandBarRef?.current?.itemsRef.current || []);

      const contextualMenuProps: IContextualMenuProps = {
        target: ev?.target as HTMLElement,
        items:
          menuItems?.filter((item) => {
            if (item.itemType === ContextualMenuItemType.Divider) {
              return true;
            }

            const itemProps = item.data as ICommandBarItem;
            const rootStyle: any = (item.buttonStyles as IButtonStyles)?.root;
            const isItemVisible = !rootStyle || rootStyle["display"] !== "none";
            const hasMultiselectOrDependsOnSelection =
              itemProps?.multiselect !== undefined || itemProps?.dependsOnSelection;
            const isNotDisabled = !item.disabled;
            const toBeRendered =
              isItemVisible && hasMultiselectOrDependsOnSelection && isNotDisabled;

            if (toBeRendered) {
              item.onRender = (item: IContextualMenuItem) => (
                <ContextualMenuItem
                  item={item}
                  isDefault={itemProps?.isDefault}
                  afterClickCB={() => setContextualMenuProps(undefined)}
                />
              );
            }

            return toBeRendered;
          }) || [],
        onDismiss: () => {
          setContextualMenuProps(undefined);
        },
      };

      if (typeof index === "number" && index > -1) {
        setContextualMenuProps(contextualMenuProps);
      }

      return false;
    },
    [props?.itemMenu]
  );

  function renderCell(item: any, index: number | undefined, column: IOfficeColumn | undefined) {
    return renderGridCell(item, index!, column, onItemClicked, {
      intl,
      theme,
      getTypeAnnotation: typeAnnotations.getTypeAnnotation,
      getIconAnnotation: typeAnnotations.getIconAnnotation,
      propertyAnnotations: props.propertyAnnotations,
      additionalProps: {
        invokeItemByOneClick: props.schema.invokeItemByOneClick,
        dispatch: props.dispatch,
      },
    });
  }

  const handleClearQuery = useCallback(() => {
    props.dispatch({ kind: "clearQuery", previewQuery: !!props.previewQueryId });
  }, [props.dispatch, props.previewQueryId]);

  const handleAbort = useCallback(() => {
    props.abortControllerState?.abort();
    props.setAbortControllerState(null);
  }, [props.abortControllerState, props.setAbortControllerState]);

  const handleRenderDetailsHeader = useCallback(
    (propsHeader?: IDetailsHeaderProps) =>
      onRenderDetailsHeader({
        propsHeader,
        onColumnHeaderClick,
        isGrouped: Boolean(props.groups?.length),
        columnFiltersConfig: props.schema.columnFiltersConfig,
        columns: props.columns,
        columnFilters,
        onColumnFilterChange,
      }),
    [columnFilters, onColumnFilterChange, onColumnHeaderClick, props.columns, props.groups]
  );

  if (!props.items.length) {
    return (
      <BlankSearchResult
        isLoading={props.isLoading}
        hasConditions={Boolean(props.query.conditions.length)}
        onClear={handleClearQuery}
        onAbort={handleAbort}
        noItemsText={props.schema.noItemsText}
      />
    );
  }

  const renderLoading = () => {
    if (props.isLoading) {
      if (props.abortControllerState) {
        return <CancellationPanel onAbort={handleAbort} />;
      }

      return <LoadingOverlay />;
    }
    return <></>;
  };

  return (
    <>
      {renderLoading()}
      <ScrollablePane
        scrollbarVisibility="auto"
        styles={{
          root: {
            position: "relative",
            height: "100%",
            width: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            flexGrow: 1,
          },
        }}
      >
        <DetailsList
          setKey="id"
          columns={props.columns}
          items={filteredItems}
          groups={filteredGroups}
          selection={selection}
          groupProps={groupProps}
          columnReorderOptions={getColumnReorderOptions()}
          onItemContextMenu={onItemContextMenu}
          compact={props?.schema?.compact}
          componentRef={gridRef}
          selectionMode={SelectionMode[props.schema?.selectionMode] || SelectionMode.multiple}
          checkboxVisibility={
            props?.schema?.compact ? CheckboxVisibility.hidden : CheckboxVisibility.onHover
          }
          constrainMode={ConstrainMode.unconstrained}
          layoutMode={DetailsListLayoutMode.fixedColumns}
          styles={detailsListStyles(props.schema.compact, props.schema.invokeItemByOneClick)}
          onColumnHeaderClick={onColumnHeaderClick}
          onItemInvoked={onItemClick}
          onRenderDetailsHeader={handleRenderDetailsHeader}
          onRenderRow={(p, r) => renderRow(p!, r!, theme.cayoTheme)}
          onRenderItemColumn={renderCell}
          onRenderDetailsFooter={() => {
            if (!props.skipToken || props.schema.noPumping) {
              return null;
            }

            return (
              <DefaultButton
                styles={{
                  root: {
                    width: "100%",
                    border: "none",
                    position: "absolute",
                    bottom: 0,
                  },
                }}
                onClick={() => props.dispatch({ kind: "loadMore" })}
              >
                <Link
                  styles={{
                    root: {
                      fontSize: 13,
                      textDecoration: "underline",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                    },
                  }}
                >
                  <FormattedMessage
                    id="details-list.footer.get-more"
                    defaultMessage={"Load more"}
                  />
                </Link>
              </DefaultButton>
            );
          }}
          onColumnResize={onColumnResize}
        />
        {contextualMenuProps && <ContextualMenu {...contextualMenuProps} />}
      </ScrollablePane>
    </>
  );
}

const groupProps: IDetailsGroupRenderProps = {
  headerProps: {
    styles: {
      title: { fontWeight: 600, fontSize: 12 },
      groupHeaderContainer: { background: "rgba(37, 198, 255, 0.2)", height: 38 },
      expand: { height: 38 },
    },
  },
};

interface RenderDetailsHeaderParams {
  propsHeader?: IDetailsHeaderProps;
  onColumnHeaderClick?: (_?: React.MouseEvent<HTMLElement>, column?: IOfficeColumn) => void;
  columnFiltersConfig?: GridColumnFiltersConfig;
  isGrouped: boolean;
  columns?: ReadonlyArray<IOfficeColumn>;
  columnFilters?: ColumnFiltersState;
  onColumnFilterChange: OnColumnFilterChange;
}

const onRenderDetailsHeader = ({
  propsHeader,
  onColumnHeaderClick,
  columnFiltersConfig,
  isGrouped,
  columns = [],
  columnFilters,
  onColumnFilterChange,
}: RenderDetailsHeaderParams): JSX.Element => {
  const newPropsHeader = { ...propsHeader };

  return (
    <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced={true}>
      <div>
        <DetailsHeader
          styles={{
            root: {
              paddingTop: 0,
              fontSize: "12px !important",
              selectors: {
                ".ms-DetailsHeader-cellName": {
                  fontSize: 12,
                },
                ".ms-DetailsHeader-filterChevron": {
                  display: "none",
                },
                ".ms-DetailsRow-check": {
                  width: "32px !important",
                },
                ".ms-DetailsHeader-cellIsCheck": {
                  width: "32px !important",
                },
              },
            },
          }}
          {...newPropsHeader}
          layoutMode={propsHeader?.layoutMode || DetailsListLayoutMode.fixedColumns}
          onRenderColumnHeaderTooltip={(props: any, r) => {
            const hasTooltip = props?.column?.isIconOnly && props?.column?.ariaLabel;
            const isSortedDescending = props?.column?.isSortedDescending;
            const columnActionsMode = props?.column?.columnActionsMode;

            return (
              <div
                onClick={(e) =>
                  hasTooltip &&
                  columnActionsMode &&
                  onColumnHeaderClick &&
                  onColumnHeaderClick(e, props?.column)
                }
              >
                {hasTooltip && (
                  <HeaderWrap>
                    <TooltipHost
                      tooltip={props.column.ariaLabel}
                      directionalHint={DirectionalHint.topCenter}
                      showCopyButton={false}
                    >
                      <div>
                        <Icon iconName={props.column.iconName} />
                        {isSortedDescending !== undefined && (
                          <Icon iconName={isSortedDescending ? "SortUp" : "SortDown"} />
                        )}
                      </div>
                    </TooltipHost>
                  </HeaderWrap>
                )}

                {!hasTooltip && <>{r!(props)}</>}
              </div>
            );
          }}
        />
        {columnFiltersConfig?.enabled && (
          <ColumnFilters
            columns={columns}
            columnFilters={columnFilters}
            isGrouped={isGrouped}
            onColumnFilterChange={onColumnFilterChange}
          />
        )}
      </div>
    </Sticky>
  );
};

const detailsListStyles = (
  compact?: boolean,
  invokeItemByOneClick?: boolean
): Partial<IDetailsListStyles> => {
  const paddingTop = compact ? "8px !important" : "12px !important";
  const paddingBottom = compact ? "0px !important" : "8px !important";
  return {
    root: {
      paddingTop: "0 !important",
      selectors: {
        ".ms-DetailsHeader-filterChevron": {
          display: "none",
        },
        ".ms-DetailsRow-check": {
          width: "32px !important",
        },

        ".ms-DetailsHeader-cellIsCheck": {
          width: "32px !important",
        },
        ".ms-DetailsRow-fields": {
          display: "block",
          cursor: invokeItemByOneClick ? "pointer" : undefined,
          selectors: {
            ".ms-DetailsRow-cell": {
              paddingTop: paddingTop,
              paddingBottom: paddingBottom,
              paddingLeft: 8,
              paddingRight: 8,
              height: "100%",
            },
          },
        },
      },
    },

    contentWrapper: {
      selectors: {
        ".ms-DetailsRow": {
          width: "100%",
        },
        ".ms-DetailsRow .cs-ShowOnHover": {
          visibility: "hidden",
        },
        ".ms-DetailsRow:hover .cs-ShowOnHover": {
          visibility: "visible",
        },
      },
    },

    headerWrapper: {
      fontSize: "8px",
      selectors: {
        ".ms-DetailsHeader-cell": {
          selectors: {
            ":hover": {
              border: "1px solid rgba(0, 0, 0, 0.2)",
            },
          },
        },
        ".ms-DetailsHeader-cellSizer": {
          selectors: {
            ":after": { width: 3 },
          },
        },
        ".ms-DetailsHeader": {
          paddingTop: 0,
          width: "100%",
        },
      },
    },
  };
};

const HeaderWrap = styled.div`
  display: flex;

  .ms-TooltipHost {
    justify-content: center;
    width: 100%;

    i[data-icon-name="Complexity"] {
      padding-top: 4px;
    }
  }
`;

// function setSort(columns: IOfficeColumn[], fieldName: string, asc: boolean) {
//   log.debug("setSort -> fieldName, asc", fieldName, asc);
//   columns.forEach((c) => {
//     const cc = c as unknown as IColumn;
//     if ((cc?.sortingFieldName ?? c.fieldName) === fieldName) {
//       c.isSorted = true;
//       c.isSortedDescending = !asc;
//     } else {
//       c.isSorted = false;
//       c.isSortedDescending = undefined;
//     }
//   });
// }
